import React, { useState, useEffect, useRef, useContext, useMemo } from 'react'
import styled from 'styled-components'
import i18n from 'live/services/i18n'
import { GoogleMapsContext } from './GoogleMapsProvider'
import { mapDefault, mapLight } from './mapUtils';

export type LayerProp = {
    id: number
    title: string
    url: string
    type: string
}

const MAP_ROAD: LayerProp =  { id: 1, title: 'Default', type: 'roadmap', url: '/content/plugin-extension-google/default-type.png' }
const MAP_LIGHT: LayerProp = { id: 2, title: 'Light', type: 'roadmap', url: '/content/plugin-extension-google/light-type.png' }
const MAP_HYBRID: LayerProp = { id: 3, title: 'Satellite', type: 'hybrid', url: '/content/plugin-extension-google/hybrid-type.png' }           
const MAP_TRANSIT: LayerProp = { id: 4, title: 'Transit', type: 'transit', url: '/content/plugin-extension-google/transit-type.png' }
const MAP_TRAFFIC: LayerProp = { id: 5, title: 'Traffic',  type: 'traffic', url: '/content/plugin-extension-google/traffic-type.png' }
const MAP_TERRAIN: LayerProp = { id: 6, title: 'Terrain', type: 'terrain', url: '/content/plugin-extension-google/terrain-type.png' }

export {MAP_HYBRID, MAP_LIGHT, MAP_ROAD, MAP_TERRAIN, MAP_TRAFFIC}

const LayerType = ({layer, activeLayer, click}) => {
    const {url, title, id} = layer

    return <TypeOpt active={activeLayer && id === activeLayer.id} onClick={click}>
        <img src={url}></img>
        <span>{i18n(title)}</span>
    </TypeOpt>
}

export const LayersMenu = (): JSX.Element => {

    const menuRef = useRef(null)
    const {mapInstance, trafficLayer, transitLayer, layer} = useContext(GoogleMapsContext)


    const [openMenu, setOpenMenu] = useState(false)
    const [layerSelected, setLayerSelected] = useState(MAP_ROAD)
    const [detailSelected, setDetailSelected] = useState<LayerProp | undefined>()

    useEffect(() => {
        if (mapInstance != null) {      
            mapInstance.addListener('click', () => {
                setOpenMenu(false)
            })
        }

        const handleClickOutside = (event) => {            
          if (menuRef.current && !menuRef.current.contains(event.target)) {
            setOpenMenu(false);
          }
        }
    
        document.body.addEventListener('click', handleClickOutside);
    
        return () => {
          document.body.removeEventListener('click', handleClickOutside);
        };
    }, []);

    const changeDetailSelected = (layer) => {

        if(layer === detailSelected) setDetailSelected(undefined)
        else                         setDetailSelected(layer)
    }

    const defineMapLight = (event) => {
        if(!mapInstance) return

        if(detailSelected?.id === MAP_TERRAIN.id) setDetailSelected(undefined)

        mapInstance.setOptions(mapLight)
        mapInstance.setMapTypeId(MAP_LIGHT.type)
        setLayerSelected(MAP_LIGHT)
    }

    const defineMapDefault = (event) => {

        if(!mapInstance) return

        if(detailSelected?.id === MAP_TERRAIN.id) setDetailSelected(undefined)

        mapInstance.setOptions(mapDefault)
        mapInstance.setMapTypeId(MAP_ROAD.type)
        setLayerSelected(MAP_ROAD)
    }

    const defineMapSatellite = (event) => {
        
        if(!mapInstance) return

        if(detailSelected?.id === MAP_TERRAIN.id) setDetailSelected(undefined)

        mapInstance.setOptions(mapDefault)
        mapInstance.setMapTypeId(MAP_HYBRID.type)
        setLayerSelected(MAP_HYBRID)
    }

    const defineTraffic = (event) => {

        if(!mapInstance) return

        const enable = detailSelected?.id !== MAP_TRAFFIC.id

        transitLayer.setMap(null)

        if(enable) trafficLayer.setMap(mapInstance)        
        else       trafficLayer.setMap(null)        
        
        changeDetailSelected(MAP_TRAFFIC)
        mapInstance.setMapTypeId(layerSelected.type)
    }

    // const defineTransit = (event) => {

    //     if(!mapInstance) return

    //     const enable = detailSelected?.id !== MAP_TRANSIT.id

    //     trafficLayer.setMap(null)

    //     if(enable) transitLayer.setMap(mapInstance)
    //     else       transitLayer.setMap(null)
        
    //     changeDetailSelected(MAP_TRANSIT)        
    //     mapInstance.setMapTypeId(layerSelected.type)
    // }

    const defineTerrain = (event) => {

        if(!mapInstance) return

        transitLayer.setMap(null)
        trafficLayer.setMap(null)

        const enable = detailSelected?.id !== MAP_TERRAIN.id
        
        if(enable) mapInstance.setMapTypeId(MAP_TERRAIN.type)
        else       mapInstance.setMapTypeId(layerSelected.type)
        
        changeDetailSelected(MAP_TERRAIN)
    }

    const chooseInitialMap = (id: number) => {
        if(id == MAP_HYBRID.id)     defineMapSatellite(undefined)
        else if(id == MAP_LIGHT.id) defineMapLight(undefined)
        else if(id == MAP_ROAD.id)  defineMapDefault(undefined)
    }

    useEffect(() => {
        if(layer && mapInstance) chooseInitialMap(layer?.id)
    }, [layer, mapInstance])

    return (
        <Wrapper>
            <img style={{cursor:'pointer'}} src={layerSelected.url} onClick={() => setOpenMenu(!openMenu)}/>

            {
                openMenu && 
                <WrapperPopup ref={menuRef}>
                    <Layers>
                        <Layer>
                            <span className="layer-title">{i18n('Map type')}</span>

                            <div style={{display: 'flex'}}>
                                <LayerType layer={MAP_ROAD} activeLayer={layerSelected} click={defineMapDefault} />
                                <LayerType layer={MAP_LIGHT} activeLayer={layerSelected} click={defineMapLight} />
                                <LayerType layer={MAP_HYBRID} activeLayer={layerSelected} click={defineMapSatellite} />
                            </div>
                        </Layer>


                        <Layer>
                            <span className="layer-title">{i18n('Map details')}</span>

                            <div style={{display: 'flex'}}>
                                <LayerType layer={MAP_TRAFFIC} activeLayer={detailSelected} click={defineTraffic}/>                                
                                {/* <LayerType layer={MAP_TRANSIT} activeLayer={detailSelected} click={defineTransit}/> */}
                                <LayerType layer={MAP_TERRAIN} activeLayer={detailSelected} click={defineTerrain}/>
                            </div>
                        </Layer>
                    </Layers>
                </WrapperPopup>
            }
        </Wrapper>
    )
}

export default LayersMenu

const Wrapper = styled.div`
    box-shadow: 0px 2px 4px -1px rgba(0,0,0,0.06), 0px 4px 6px -1px rgba(0,0,0,0.10);
    border-radius: 9px;
    border: 1px solid #fff;
`

const WrapperPopup = styled.div`
    border-radius: 4px;
    top: 50px;
    position: absolute;
    z-index: 1;
    right: 0px;
    padding: 12px;
    background: ${({theme}) => theme['gray-0']};
    box-shadow: 0px 2px 4px -1px rgba(0, 0, 0, 0.06), 0px 4px 6px -1px rgba(0, 0, 0, 0.10);
`

const Layers = styled.div`
    display: flex;
`

const Layer = styled.div`
    &:first-of-type{
        padding-left: 8px;
        margin-right: 16px;
        border-right: 1px solid ${({theme}) => theme['gray-400']};
    }

    .layer-title{
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-size: 16px;
        font-weight: 600;
    }
`

const TypeOpt = styled.div`
    display: flex;
    flex-direction: column;
    padding: 10px;
    cursor: pointer;
    align-items: center;

    &:first-of-type{
        padding-left: 0;
    }

    img {        
        height: 50px;
        width: 50px;
        border-radius: 10px;
        border: 2px solid ${({theme, active}) => active ? theme['blue-500'] : theme['gray-100']};
    }

    span {
        margin-top: 10px;
        font-size: 13px;
        color: ${({theme, active}) => active ? theme['blue-500'] : theme['gray-700']};
        font-weight: ${({active}) => active ? 600 : 'normal'};         
    }
`
